import React, { useState } from 'react';
import {
  CUSTOM_PAYMENT_TERMS,
  MAX_NET_DAYS,
  PRESET_PAYMENT_TERMS,
  customDaysToPaymentTerms,
  paymentTermsDays,
  paymentTermsOptions,
  toCanonicalPaymentTerms,
} from '../../utils/paymentTerms';

// The ONE payment-terms picker (2026-10-20). Presets (Due on receipt, Net 7/15/30/45/60/90)
// plus Custom (1..365 days). `value` may be any stored spelling; `onChange` always receives
// the canonical code ('due_on_receipt' | 'net_N'), so every writer stores the same form.
// An unreadable stored value shows the company-default placeholder rather than a wrong pick.
export default function PaymentTermsSelect({
  value,
  onChange,
  id,
  className = '',
  inputClassName = '',
  optionClassName,
  disabled = false,
  'data-testid': testId = 'payment-terms-select',
}) {
  const canonical = toCanonicalPaymentTerms(value);
  const [customOpen, setCustomOpen] = useState(false);
  const [customDays, setCustomDays] = useState('');

  const options = paymentTermsOptions(canonical);
  const selectValue = customOpen ? CUSTOM_PAYMENT_TERMS : (canonical || '');

  const handleSelect = (e) => {
    const next = e.target.value;
    if (next === CUSTOM_PAYMENT_TERMS) {
      setCustomOpen(true);
      const d = paymentTermsDays(canonical);
      setCustomDays(canonical && !PRESET_PAYMENT_TERMS.includes(canonical) && d ? String(d) : '');
      return;
    }
    setCustomOpen(false);
    if (next) onChange(next);
  };

  const handleCustomDays = (e) => {
    const raw = e.target.value.replace(/[^0-9]/g, '').slice(0, 3);
    setCustomDays(raw);
    // Custom is 1..365 days only; 0 is the "Due on receipt" preset, not a custom term.
    const n = Number(raw);
    if (raw !== '' && n >= 1 && n <= MAX_NET_DAYS) onChange(customDaysToPaymentTerms(n));
  };

  const customDaysInvalid = customOpen && customDays !== ''
    && !(Number(customDays) >= 1 && Number(customDays) <= MAX_NET_DAYS);

  return (
    <div>
      <select
        id={id}
        value={selectValue}
        onChange={handleSelect}
        disabled={disabled}
        className={className}
        data-testid={testId}
      >
        {!canonical && !customOpen && <option value="" className={optionClassName}>Select payment terms</option>}
        {options.map((o) => (
          <option key={o.value} value={o.value} className={optionClassName}>{o.label}</option>
        ))}
      </select>
      {customOpen && (
        <div className="mt-2 flex items-center gap-2">
          <span className="text-sm text-gray-600 dark:text-gray-300">Net</span>
          <input
            type="text"
            inputMode="numeric"
            value={customDays}
            onChange={handleCustomDays}
            disabled={disabled}
            placeholder="days"
            aria-label="Custom payment terms, number of days"
            className={inputClassName || 'w-24 px-2 py-1 border border-gray-300 rounded-md text-sm'}
            data-testid={`${testId}-custom-days`}
          />
          <span className="text-sm text-gray-600 dark:text-gray-300">days (1–{MAX_NET_DAYS})</span>
        </div>
      )}
      {customDaysInvalid && (
        <p className="mt-1 text-xs text-red-600">Enter a number of days from 1 to {MAX_NET_DAYS}.</p>
      )}
    </div>
  );
}
